import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Form, Button, Modal, Spinner, InputGroup } from "react-bootstrap";
import Footer from "../layouts/Footer";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Méthodes de paiement
const methodesPaiement = [
  { id: "mobile_money", nom: "Paiement Mobile", icon: "bi-phone-fill", description: "Orange Money, Airtel Money", color: "#25D366", bg: "#dcfce7" },
  { id: "CB", nom: "Carte Bancaire", icon: "bi-credit-card-fill", description: "Paiement sécurisé", color: "#1a73e8", bg: "#dbeafe" },
  { id: "virement", nom: "Virement Bancaire", icon: "bi-bank", description: "Virement traditionnel", color: "#6c757d", bg: "#f1f5f9" }
];

// Packs prédéfinis
const packsPredefinis = [
  { code_pack: 1, pack_name: "Pack Starter", pack_sms: 1000, is_popular: 0, icon: "bi-rocket-takeoff-fill", color: "#3b82f6", bgColor: "#eff6ff" },
  { code_pack: 2, pack_name: "Pack Business", pack_sms: 10000, is_popular: 1, icon: "bi-briefcase-fill", color: "#f59e0b", bgColor: "#fef3c7" },
  { code_pack: 3, pack_name: "Pack Enterprise", pack_sms: 50000, is_popular: 0, icon: "bi-building-fill", color: "#10b981", bgColor: "#d1fae5" },
  { code_pack: 4, pack_name: "Pack Premium", pack_sms: 100000, is_popular: 0, icon: "bi-gem", color: "#8b5cf6", bgColor: "#ede9fe" }
];

export default function RechargeSolde() {
  const navigate = useNavigate();

  const [methodePaiement, setMethodePaiement] = useState("mobile_money");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSuccesModal, setShowSuccesModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [numeroMobile, setNumeroMobile] = useState("");
  const [rechercheLoading, setRechercheLoading] = useState(false);

  const [networkName, setNetworkName] = useState("");
  const [networkLogo, setNetworkLogo] = useState("");
  const [networkCode, setNetworkCode] = useState("");
  const [needOtp, setNeedOtp] = useState(false);
  const [walletsSupplementaires, setWalletsSupplementaires] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState("");

  const [tarifs, setTarifs] = useState([]);
  const [paysList, setPaysList] = useState([]);
  const [error, setError] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const [carteInfo, setCarteInfo] = useState({ numero: "", nom: "", expiration: "", cvv: "" });

  const { business } = useAuth();

  const [virementInfo, setVirementInfo] = useState({ banque: "", numeroCompte: "", reference: "" });

  const [paysSelectionne, setPaysSelectionne] = useState("");
  const [tarifSelectionne, setTarifSelectionne] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: "", type: "info" });
  const [nombreSMSInput, setNombreSMSInput] = useState("");
  const [activeTab, setActiveTab] = useState("packs");

  const [solde, setSolde] = useState(0);
  const [soldeV1, setSoldeV1] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loadingSolde, setLoadingSolde] = useState(false);

  const [packDetails, setPackDetails] = useState({
    sms_quantity: 0,
    credits_granted: 0,
    total_amount: 0,
    reference_price: 0
  });

  const initDoneRef = useRef(false);

  // ============================================
  // FETCH SOLDE
  // ============================================
  const fetchSolde = async () => {
    try {
      setLoadingSolde(true);
      setError(null);

      const response = await apiClient.get('/sms-balance');

      const balanceV1 = response.data.balance_v1 && response.data.balance_v1.length > 0
        ? response.data.balance_v1[0]
        : null;

      const balance = response.data.balance;

      if (balanceV1) {
        setSolde(parseFloat(balanceV1.solde) || 0);
        if (balanceV1.last_update) setLastUpdate(balanceV1.last_update);
      } else {
        setSolde(0);
      }

      if (balance) {
        setSoldeV1(parseFloat(balance.solde_v1) || 0);
        if (balance.derniere_maj) setLastUpdate(balance.derniere_maj);
      } else {
        setSoldeV1(0);
      }
    } catch (err) {
      console.error("❌ Erreur solde:", err);
      setSolde(0);
      setSoldeV1(0);
    } finally {
      setLoadingSolde(false);
    }
  };

  // ============================================
  // HELPERS
  // ============================================
  const extractPaysList = (tarifsData) => {
    const paysMap = new Map();

    tarifsData.forEach(tarif => {
      const countryIso = tarif.country_iso || tarif.country;
      if (countryIso && !paysMap.has(countryIso)) {
        paysMap.set(countryIso, {
          country_iso: countryIso,
          currency: tarif.currency,
          sms_price: tarif.sms_price,
          credit_rate: tarif.credit_rate
        });
      }
    });

    return Array.from(paysMap.values());
  };

  const getTarifPays = (paysCode) => {
    return tarifs.find(tarif => (tarif.country_iso || tarif.country) === paysCode);
  };

  const showNotification = (message, type = "info") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "info" }), 5000);
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant) + (tarifSelectionne ? ` ${tarifSelectionne.currency}` : ' FCFA');
  };

  const renderLogo = (logo) => {
    if (!logo) return <span className="rs-network-emoji">📱</span>;
    if (logo.startsWith('http')) {
      return <img src={logo} alt="Logo" className="rs-network-logo" />;
    }
    return <span className="rs-network-emoji">{logo}</span>;
  };

  // ============================================
  // FETCH PACK CALCULATION
  // ============================================
  const fetchPackCalculation = async (smsQuantity, country) => {
    if (!smsQuantity || smsQuantity <= 0 || !country) return;

    setLoading(true);
    try {
      const response = await apiClient.get('/sms-bundles', {
        params: { sms_quantity: smsQuantity, country_iso: country }
      });

      const data = response.data;

      if (data.success && data.data) {
        setPackDetails({
          sms_quantity: data.data.sms_quantity,
          credits_granted: data.data.credits_granted,
          total_amount: data.data.total_amount,
          reference_price: data.data.reference_price
        });

        showNotification(
          `${data.data.sms_quantity.toLocaleString()} SMS pour ${data.data.total_amount.toLocaleString()} ${tarifSelectionne?.currency || 'FCFA'}`,
          "success"
        );
      } else {
        throw new Error(data.message || 'Erreur lors du calcul');
      }
    } catch (err) {
      console.error('❌ Erreur calcul:', err);
      let errorMsg = 'Erreur de calcul. Veuillez réessayer.';
      if (err.response?.data?.message) errorMsg = err.response.data.message;
      else if (err.response?.status === 405) errorMsg = 'Service temporairement indisponible.';

      setError(errorMsg);
      showNotification(errorMsg, "danger");
      setPackDetails({ sms_quantity: 0, credits_granted: 0, total_amount: 0, reference_price: 0 });
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handlePaysChange = (e) => {
    const paysCode = e.target.value;
    setPaysSelectionne(paysCode);
    setPackDetails({ sms_quantity: 0, credits_granted: 0, total_amount: 0, reference_price: 0 });
    setNombreSMSInput("");

    if (paysCode) {
      const tarif = getTarifPays(paysCode);
      if (tarif) {
        setTarifSelectionne(tarif);
        showNotification(`Pays ${paysCode} sélectionné`, "info");
      }
    } else {
      setTarifSelectionne(null);
    }
  };

  const handleSelectPack = async (pack) => {
    if (paysSelectionne) {
      setNombreSMSInput(pack.pack_sms.toString());
      setActiveTab("packs");
      await fetchPackCalculation(pack.pack_sms, paysSelectionne);
    } else {
      showNotification("Veuillez d'abord sélectionner un pays", "warning");
    }
  };

  const handleNombreSMSChange = async (e) => {
    const value = e.target.value;

    if (value === "" || /^\d*$/.test(value)) {
      setNombreSMSInput(value);

      if (value && parseInt(value) > 0 && paysSelectionne) {
        await fetchPackCalculation(parseInt(value), paysSelectionne);
      } else if (!value) {
        setPackDetails({ sms_quantity: 0, credits_granted: 0, total_amount: 0, reference_price: 0 });
      }
    }
  };

  const fetchNetwork = async (phone) => {
    setRechercheLoading(true);
    setErrorMessage("");

    const countryCode = business?.country_code || "GA";
    const uri = `/check-payment-network?tel_number=${phone}&country=${countryCode}&type=mobile_money&source=bo`;

    try {
      const response = await apiClient.get(uri);
      const data = response.data;

      if (response.status === 200 && data.statusRequest === 200) {
        setNetworkName(data.mobile_money_svce || data.network);
        setNetworkLogo(data.mobile_money_logo);
        setNetworkCode(data.mobile_money_code);
        setNeedOtp(data.need_otp);

        const hasWallet = (data.has_wallet_1 || '').toString().toLowerCase() === 'yes';
        const wallets = [];

        if (hasWallet) {
          wallets.push({
            code: data.mobile_money_code,
            name: data.mobile_money_svce || data.network,
            logo: data.mobile_money_logo,
            type: 'principal'
          });
          wallets.push({
            code: data.wallet_code,
            name: data.wallet_name_1,
            logo: data.wallet_logo_1,
            type: 'supplementaire'
          });
        } else {
          wallets.push({
            code: data.mobile_money_code,
            name: data.mobile_money_svce || data.network,
            logo: data.mobile_money_logo,
            type: 'principal'
          });
        }

        setWalletsSupplementaires(wallets);
        setSelectedWallet(wallets[0]?.code || "");
        setErrorMessage(null);
      } else {
        const errorMsg = data.message || 'Réseau non trouvé.';
        setErrorMessage(errorMsg);
      }
    } catch (e) {
      const errorMsg = e.response?.data?.message || 'Erreur de connexion.';
      setErrorMessage(errorMsg);
    } finally {
      setRechercheLoading(false);
    }
  };

  const submitRechargeOrder = async () => {
    setLoading(true);
    try {
      const tarifPays = getTarifPays(paysSelectionne);

      if (!tarifPays) {
        setErrorMessage('Tarif non trouvé pour le pays sélectionné.');
        setShowErrorModal(true);
        setShowConfirmationModal(false);
        setLoading(false);
        return;
      }

      const requestData = {
        country: paysSelectionne,
        currency: tarifPays.currency,
        amount: packDetails.total_amount,
        client_msisdn: methodePaiement === "mobile_money" ? numeroMobile.replace(/\s/g, '') : undefined,
        payment_method: getPaymentMethodName(),
        wallet_code: methodePaiement === "mobile_money" ? selectedWallet : undefined,
        sms_quantity: packDetails.sms_quantity,
        credits_granted: packDetails.credits_granted
      };

      Object.keys(requestData).forEach(key => {
        if (requestData[key] === undefined) delete requestData[key];
      });

      const response = await apiClient.post('/order-sms', requestData);
      const data = response.data;

      if (data.code == 200) {
        setShowConfirmationModal(false);

        const paymentData = {
          transactionId: data.data.transaction_id,
          amount: packDetails.total_amount,
          paymentMethod: getPaymentMethodName(),
          phoneNumber: methodePaiement === "mobile_money" ? numeroMobile : null,
          walletCode: methodePaiement === "mobile_money" ? selectedWallet : null,
          timestamp: new Date().toISOString(),
          needOtp: needOtp,
          country: paysSelectionne,
          currency: tarifPays.currency,
          packSMS: packDetails.sms_quantity,
          creditsGranted: packDetails.credits_granted
        };

        localStorage.setItem('pendingPayment', JSON.stringify(paymentData));
        localStorage.setItem('transaction_id', data.transaction_id);

        navigate('/apps/AttenteConfirmationPaiement');
        resetForm();
      } else {
        const errorMsg = data.message || 'Erreur lors du traitement du paiement.';
        setErrorMessage(errorMsg);
        setShowErrorModal(true);
        setShowConfirmationModal(false);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erreur de connexion au serveur.';
      setErrorMessage(errorMsg);
      setShowErrorModal(true);
      setShowConfirmationModal(false);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodName = () => {
    if (methodePaiement === "mobile_money") {
      const selected = walletsSupplementaires.find(w => w.code === selectedWallet);
      return selected?.name || networkName;
    }
    return methodesPaiement.find(m => m.id === methodePaiement)?.nom;
  };

  const fetchTarifs = async () => {
    try {
      setRefreshLoading(true);
      setError(null);

      const response = await apiClient.get('/sms-pricing');

      let tarifsData = [];
      if (response.data.code === 200) tarifsData = response.data.data;
      else if (Array.isArray(response.data)) tarifsData = response.data;
      else if (response.data.data && Array.isArray(response.data.data)) tarifsData = response.data.data;
      else throw new Error('Format de réponse inattendu');

      if (tarifsData && tarifsData.length > 0) {
        setTarifs(tarifsData);
        const pays = extractPaysList(tarifsData);
        setPaysList(pays);
        showNotification(`${pays.length} pays chargés avec succès`, "success");
      } else {
        throw new Error('Aucune donnée de tarif reçue');
      }
    } catch (err) {
      console.error('❌ Erreur tarifs:', err);
      let errorMsg = 'Erreur de connexion au serveur';
      if (err.response?.data?.message) errorMsg = err.response.data.message;
      else if (err.message) errorMsg = err.message;
      setError(errorMsg);
      showNotification(errorMsg, "danger");

      const fallbackTarifs = [
        { country_iso: "GA", currency: "FCFA", sms_price: "39.00", credit_rate: "0.8666" },
        { country_iso: "CI", currency: "FCFA", sms_price: "41.00", credit_rate: "0.9111" },
        { country_iso: "SN", currency: "FCFA", sms_price: "40.00", credit_rate: "0.8888" }
      ];
      setTarifs(fallbackTarifs);
      setPaysList(extractPaysList(fallbackTarifs));
    } finally {
      setRefreshLoading(false);
    }
  };

  useEffect(() => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;
    fetchTarifs();
    fetchSolde();
  }, []);

  const resetForm = () => {
    setPaysSelectionne("");
    setTarifSelectionne(null);
    setNombreSMSInput("");
    setPackDetails({ sms_quantity: 0, credits_granted: 0, total_amount: 0, reference_price: 0 });
    setNumeroMobile("");
    setNetworkName("");
    setNetworkLogo("");
    setNetworkCode("");
    setNeedOtp(false);
    setWalletsSupplementaires([]);
    setSelectedWallet("");
    setCarteInfo({ numero: "", nom: "", expiration: "", cvv: "" });
    setVirementInfo({ banque: "", numeroCompte: "", reference: "" });
    setErrorMessage("");
  };

  useEffect(() => {
    const numeroNettoye = numeroMobile.replace(/\s/g, '');
    if (numeroNettoye.length >= 7) {
      fetchNetwork(numeroMobile);
    } else {
      setNetworkName("");
      setNetworkLogo("");
      setNetworkCode("");
      setNeedOtp(false);
      setWalletsSupplementaires([]);
      setSelectedWallet("");
      setErrorMessage("");
    }
  }, [numeroMobile]);

  const isFormValide = () => {
    const isValidAmount = packDetails.total_amount > 0;
    const isPaysValide = paysSelectionne !== "";
    const isSMSValide = packDetails.sms_quantity > 0;

    if (!isPaysValide || !isSMSValide || !isValidAmount) return false;

    if (methodePaiement === "mobile_money") {
      const isNumeroValide = numeroMobile.replace(/\s/g, '').length >= 8;
      const isReseauValide = networkCode !== "";
      const isWalletValide = selectedWallet !== "";
      return isNumeroValide && isReseauValide && isWalletValide;
    }

    if (methodePaiement === "CB") {
      return carteInfo.numero.replace(/\s/g, '').length >= 16 &&
             carteInfo.nom.length >= 2 &&
             carteInfo.expiration.length >= 5 &&
             carteInfo.cvv.length >= 3;
    }

    if (methodePaiement === "virement") {
      return virementInfo.banque.length >= 2 && virementInfo.numeroCompte.length >= 5;
    }

    return true;
  };

  return (
    <div className="rs-page main main-app p-3 p-lg-4">
      {/* ========== HEADER ========== */}
      <div className="rs-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="rs-header-icon">
              <i className="bi bi-wallet2"></i>
            </div>
            <div>
              <h3 className="rs-header-title mb-1">Recharge de solde</h3>
              <p className="rs-header-subtitle mb-0">
                Ajoutez des crédits à votre compte pour utiliser nos services
              </p>
            </div>
          </div>

          {/* Solde Card */}
          <div className="rs-balance-card">
            {loadingSolde ? (
              <div className="d-flex align-items-center gap-3">
                <Spinner animation="border" size="sm" />
                <span className="text-white-50">Chargement...</span>
              </div>
            ) : (
              <>
                <div className="rs-balance-item">
                  <div className="rs-balance-label">Solde SMS</div>
                  <div className="rs-balance-value">{new Intl.NumberFormat('fr-FR').format(solde || 0)}</div>
                </div>
                <div className="rs-balance-divider"></div>
                <div className="rs-balance-item">
                  <div className="rs-balance-label">Solde Crédit</div>
                  <div className="rs-balance-value">{new Intl.NumberFormat('fr-FR').format(soldeV1 || 0)}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ========== FORMULAIRE PRINCIPAL ========== */}
      <div className="rs-form-card">
        <div className="rs-form-header">
          <div className="d-flex align-items-center gap-2">
            <div className="rs-form-icon">
              <i className="bi bi-plus-circle"></i>
            </div>
            <div>
              <h6 className="rs-form-title mb-0">Nouvelle recharge</h6>
              <small className="rs-form-subtitle">Choisissez votre montant et méthode de paiement</small>
            </div>
          </div>
        </div>

        <div className="rs-form-body">
          {/* SECTION 1 : CONFIGURATION */}
          <div className="rs-section">
            <div className="rs-section-title">
              <span className="rs-section-number">1</span>
              <span>Configuration</span>
            </div>

            <div className="rs-form-group">
              <label className="rs-form-label">
                Pays de destination <span className="rs-required">*</span>
              </label>
              <div className="rs-input-wrapper">
                <i className="bi bi-flag rs-input-icon"></i>
                <select
                  className="rs-select"
                  value={paysSelectionne}
                  onChange={handlePaysChange}
                  disabled={refreshLoading}
                >
                  <option value="">Sélectionnez un pays</option>
                  {paysList.map((pays) => (
                    <option key={pays.country_iso} value={pays.country_iso}>
                      {pays.country_iso} - {pays.currency}
                    </option>
                  ))}
                </select>
                {refreshLoading && (
                  <div className="rs-input-spinner">
                    <Spinner animation="border" size="sm" />
                  </div>
                )}
              </div>
            </div>

            {/* Onglets */}
            <div className="rs-tabs">
              <button
                className={`rs-tab ${activeTab === "packs" ? "active" : ""}`}
                onClick={() => setActiveTab("packs")}
              >
                <i className="bi bi-grid-3x3-gap-fill"></i>
                Packs SMS
              </button>
              <button
                className={`rs-tab ${activeTab === "custom" ? "active" : ""}`}
                onClick={() => setActiveTab("custom")}
              >
                <i className="bi bi-pencil-square"></i>
                Saisir le nombre
              </button>
            </div>

            {/* Contenu des onglets */}
            <div className="rs-tab-content">
              {activeTab === "packs" && (
                <div className="rs-packs-grid">
                  {packsPredefinis.map((pack) => {
                    const isSelected = packDetails.sms_quantity === pack.pack_sms;
                    return (
                      <div
                        key={pack.code_pack}
                        className={`rs-pack-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectPack(pack)}
                        style={{
                          '--pack-color': pack.color,
                          '--pack-bg': pack.bgColor,
                          borderColor: isSelected ? pack.color : '#e2e8f0'
                        }}
                      >
                        {pack.is_popular === 1 && (
                          <div className="rs-pack-badge-popular">
                            <i className="bi bi-star-fill"></i>
                            Populaire
                          </div>
                        )}
                        <div className="rs-pack-icon" style={{ background: pack.bgColor, color: pack.color }}>
                          <i className={`bi ${pack.icon}`}></i>
                        </div>
                        <div className="rs-pack-name">{pack.pack_name}</div>
                        <div className="rs-pack-sms" style={{ color: pack.color }}>
                          {pack.pack_sms.toLocaleString()}
                        </div>
                        <div className="rs-pack-label">SMS</div>
                        {isSelected && (
                          <div className="rs-pack-check" style={{ background: pack.color }}>
                            <i className="bi bi-check-lg"></i>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {activeTab === "custom" && (
                <div className="rs-custom-section">
                  <div className="rs-custom-icon">
                    <i className="bi bi-envelope-paper"></i>
                  </div>
                  <h5 className="rs-custom-title">Personnalisez votre recharge</h5>
                  <p className="rs-custom-text">
                    Saisissez le nombre exact de SMS que vous souhaitez acheter
                  </p>
                  <div className="rs-custom-input-wrapper">
                    <i className="bi bi-envelope-paper rs-input-icon"></i>
                    <input
                      type="text"
                      className="rs-input rs-input-with-suffix"
                      placeholder="Exemple: 10000"
                      value={nombreSMSInput}
                      onChange={handleNombreSMSChange}
                      disabled={!paysSelectionne}
                    />
                    <span className="rs-input-suffix">SMS</span>
                  </div>
                  {!paysSelectionne && (
                    <div className="rs-alert rs-alert-warning mt-3">
                      <i className="bi bi-info-circle"></i>
                      Veuillez d'abord sélectionner un pays
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Récapitulatif */}
            {packDetails.total_amount > 0 && (
              <div className="rs-summary">
                <div className="rs-summary-header">
                  <i className="bi bi-check-circle-fill"></i>
                  <span>Récapitulatif de votre commande</span>
                </div>
                <div className="rs-summary-items">
                  <div className="rs-summary-item">
                    <i className="bi bi-envelope-paper-fill"></i>
                    <span className="rs-summary-value">{packDetails.sms_quantity.toLocaleString()}</span>
                    <span className="rs-summary-label">SMS</span>
                  </div>
                  <div className="rs-summary-item">
                    <i className="bi bi-star-fill"></i>
                    <span className="rs-summary-value">{packDetails.credits_granted.toLocaleString()}</span>
                    <span className="rs-summary-label">Crédits</span>
                  </div>
                  <div className="rs-summary-item rs-summary-item-price">
                    <i className="bi bi-cash-stack"></i>
                    <span className="rs-summary-value">{packDetails.total_amount.toLocaleString()}</span>
                    <span className="rs-summary-label">{tarifSelectionne?.currency || 'FCFA'}</span>
                  </div>
                </div>
                <div className="rs-summary-footer">
                  Prix de référence: <strong>{packDetails.reference_price} {tarifSelectionne?.currency}/SMS</strong>
                </div>
              </div>
            )}

            {/* Notification */}
            {notification.show && (
              <div className={`rs-alert rs-alert-${notification.type} mt-3`}>
                <i className={`bi ${
                  notification.type === "success" ? "bi-check-circle" :
                  notification.type === "danger" ? "bi-exclamation-triangle" : "bi-info-circle"
                }`}></i>
                {notification.message}
              </div>
            )}
          </div>

          {/* SECTION 2 : MÉTHODE DE PAIEMENT */}
          <div className="rs-section">
            <div className="rs-section-title">
              <span className="rs-section-number">2</span>
              <span>Méthode de paiement</span>
            </div>

            <div className="rs-methods-grid">
              {methodesPaiement.map(methode => {
                const isSelected = methodePaiement === methode.id;
                return (
                  <div
                    key={methode.id}
                    className={`rs-method-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => setMethodePaiement(methode.id)}
                    style={{
                      borderColor: isSelected ? methode.color : '#e2e8f0',
                      background: isSelected ? methode.bg : 'white'
                    }}
                  >
                    <div className="rs-method-icon" style={{ color: isSelected ? methode.color : '#94a3b8' }}>
                      <i className={`bi ${methode.icon}`}></i>
                    </div>
                    <div className="rs-method-name">{methode.nom}</div>
                    <div className="rs-method-desc">{methode.description}</div>
                    {isSelected && (
                      <div className="rs-method-check" style={{ color: methode.color }}>
                        <i className="bi bi-check-circle-fill"></i>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* SECTION 3 : DÉTAILS DU PAIEMENT */}
          {methodePaiement === "mobile_money" && (
            <div className="rs-section rs-details-section">
              <div className="rs-section-title">
                <span className="rs-section-number">3</span>
                <span>Informations de paiement mobile</span>
              </div>

              <div className="rs-form-group">
                <label className="rs-form-label">
                  Numéro de téléphone <span className="rs-required">*</span>
                </label>
                <div className="rs-input-wrapper">
                  <i className="bi bi-telephone rs-input-icon"></i>
                  <input
                    type="text"
                    className="rs-input"
                    placeholder="077 23 45 67"
                    value={numeroMobile}
                    onChange={(e) => setNumeroMobile(e.target.value)}
                    maxLength={14}
                  />
                  {rechercheLoading && (
                    <div className="rs-input-spinner">
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}
                </div>
              </div>

              {walletsSupplementaires.length > 0 && (
                <div className="rs-form-group">
                  <label className="rs-form-label">Mode de paiement</label>
                  <div className="rs-wallets-grid">
                    {walletsSupplementaires.map((wallet) => {
                      const isSelected = selectedWallet === wallet.code;
                      return (
                        <div
                          key={wallet.code}
                          className={`rs-wallet-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => setSelectedWallet(wallet.code)}
                        >
                          <div className="rs-wallet-logo">{renderLogo(wallet.logo)}</div>
                          <div className="rs-wallet-info">
                            <div className="rs-wallet-name">{wallet.name}</div>
                            <div className="rs-wallet-code">Code: {wallet.code}</div>
                          </div>
                          {isSelected && (
                            <i className="bi bi-check-circle-fill rs-wallet-check"></i>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="rs-alert rs-alert-warning mt-3">
                  <i className="bi bi-exclamation-triangle"></i>
                  {errorMessage}
                </div>
              )}
            </div>
          )}

          {methodePaiement === "CB" && (
            <div className="rs-section rs-details-section">
              <div className="rs-section-title">
                <span className="rs-section-number">3</span>
                <span>Informations de carte bancaire</span>
              </div>

              <div className="rs-form-group">
                <label className="rs-form-label">Numéro de carte</label>
                <input
                  type="text"
                  className="rs-input"
                  placeholder="1234 5678 9012 3456"
                  value={carteInfo.numero}
                  onChange={(e) => setCarteInfo({ ...carteInfo, numero: e.target.value })}
                  maxLength={19}
                />
              </div>

              <div className="rs-form-row">
                <div className="rs-form-group">
                  <label className="rs-form-label">Nom sur la carte</label>
                  <input
                    type="text"
                    className="rs-input"
                    placeholder="JEAN DUPONT"
                    value={carteInfo.nom}
                    onChange={(e) => setCarteInfo({ ...carteInfo, nom: e.target.value })}
                  />
                </div>
                <div className="rs-form-group">
                  <label className="rs-form-label">Expiration</label>
                  <input
                    type="text"
                    className="rs-input"
                    placeholder="MM/AA"
                    value={carteInfo.expiration}
                    onChange={(e) => setCarteInfo({ ...carteInfo, expiration: e.target.value })}
                    maxLength={5}
                  />
                </div>
                <div className="rs-form-group">
                  <label className="rs-form-label">CVV</label>
                  <input
                    type="text"
                    className="rs-input"
                    placeholder="123"
                    value={carteInfo.cvv}
                    onChange={(e) => setCarteInfo({ ...carteInfo, cvv: e.target.value })}
                    maxLength={3}
                  />
                </div>
              </div>
            </div>
          )}

          {methodePaiement === "virement" && (
            <div className="rs-section rs-details-section">
              <div className="rs-section-title">
                <span className="rs-section-number">3</span>
                <span>Informations de virement bancaire</span>
              </div>

              <div className="rs-form-row">
                <div className="rs-form-group">
                  <label className="rs-form-label">Nom de la banque</label>
                  <input
                    type="text"
                    className="rs-input"
                    placeholder="Ex: BGFI Bank"
                    value={virementInfo.banque}
                    onChange={(e) => setVirementInfo({ ...virementInfo, banque: e.target.value })}
                  />
                </div>
                <div className="rs-form-group">
                  <label className="rs-form-label">Numéro de compte</label>
                  <input
                    type="text"
                    className="rs-input"
                    placeholder="Numéro de compte"
                    value={virementInfo.numeroCompte}
                    onChange={(e) => setVirementInfo({ ...virementInfo, numeroCompte: e.target.value })}
                  />
                </div>
              </div>

              <div className="rs-form-group">
                <label className="rs-form-label">Référence (optionnel)</label>
                <input
                  type="text"
                  className="rs-input"
                  placeholder="Référence du virement"
                  value={virementInfo.reference}
                  onChange={(e) => setVirementInfo({ ...virementInfo, reference: e.target.value })}
                />
              </div>
            </div>
          )}

          {/* Bouton de recharge */}
          <div className="rs-action-section">
            <button
              className="rs-btn-primary rs-btn-large"
              disabled={!isFormValide() || loading}
              onClick={() => setShowConfirmationModal(true)}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Calcul en cours...
                </>
              ) : (
                <>
                  <i className="bi bi-lightning-charge-fill"></i>
                  Payer {formatMontant(packDetails.total_amount)}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ========== MODAL CONFIRMATION ========== */}
      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)} centered className="rs-modal">
        <div className="rs-modal-header rs-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="rs-modal-icon rs-modal-icon-blue">
              <i className="bi bi-shield-check"></i>
            </div>
            <div>
              <h5 className="rs-modal-title mb-0">Confirmer la recharge</h5>
              <small className="rs-modal-subtitle">Vérifiez les informations</small>
            </div>
          </div>
          <button className="rs-modal-close" onClick={() => setShowConfirmationModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="rs-modal-body">
          <div className="rs-confirm-amount">
            <div className="rs-confirm-amount-label">Montant à payer</div>
            <div className="rs-confirm-amount-value">{formatMontant(packDetails.total_amount)}</div>
          </div>

          <div className="rs-confirm-details">
            <div className="rs-confirm-row">
              <span className="rs-confirm-label">
                <i className="bi bi-envelope-paper"></i>
                SMS
              </span>
              <span className="rs-confirm-value">{packDetails.sms_quantity.toLocaleString()}</span>
            </div>
            <div className="rs-confirm-row">
              <span className="rs-confirm-label">
                <i className="bi bi-star"></i>
                Crédits
              </span>
              <span className="rs-confirm-value text-success">{packDetails.credits_granted.toLocaleString()}</span>
            </div>
            <div className="rs-confirm-row">
              <span className="rs-confirm-label">
                <i className="bi bi-flag"></i>
                Pays
              </span>
              <span className="rs-confirm-value">{paysSelectionne}</span>
            </div>
            <div className="rs-confirm-row">
              <span className="rs-confirm-label">
                <i className="bi bi-credit-card"></i>
                Méthode
              </span>
              <span className="rs-confirm-value">{methodesPaiement.find(m => m.id === methodePaiement)?.nom}</span>
            </div>
          </div>
        </Modal.Body>

        <div className="rs-modal-footer">
          <button className="rs-btn-secondary" onClick={() => setShowConfirmationModal(false)}>
            Annuler
          </button>
          <button className="rs-btn-primary" onClick={submitRechargeOrder} disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                Traitement...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i>
                Confirmer
              </>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL SUCCÈS ========== */}
      <Modal show={showSuccesModal} onHide={() => setShowSuccesModal(false)} centered className="rs-modal">
        <Modal.Body className="rs-success-body">
          <div className="rs-success-icon">
            <i className="bi bi-check-circle-fill"></i>
          </div>
          <h4 className="rs-success-title">Paiement réussi !</h4>
          <p className="rs-success-text">
            Vous avez reçu <strong>{packDetails.sms_quantity.toLocaleString()} SMS</strong><br />
            soit <strong className="text-success">{packDetails.credits_granted.toLocaleString()} crédits</strong>
          </p>
        </Modal.Body>
      </Modal>

      {/* ========== MODAL ERREUR ========== */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered className="rs-modal">
        <Modal.Body className="rs-error-body">
          <div className="rs-error-icon">
            <i className="bi bi-exclamation-triangle-fill"></i>
          </div>
          <h4 className="rs-error-title">Erreur</h4>
          <p className="rs-error-text">{errorMessage || "Une erreur est survenue."}</p>
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .rs-page {
          background: #f8fafc;
          min-height: 100vh;
        }
        .rs-page * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ========== HEADER ========== */
        .rs-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px;
          padding: 24px 28px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .rs-header-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 8px 20px -6px rgba(59, 130, 246, 0.5);
        }
        .rs-header-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.5rem;
          letter-spacing: -0.02em;
        }
        .rs-header-subtitle {
          color: #64748b;
          font-size: 0.875rem;
        }

        /* Balance Card */
        .rs-balance-card {
          display: flex;
          align-items: center;
          gap: 20px;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          border-radius: 14px;
          padding: 16px 24px;
          color: white;
          box-shadow: 0 8px 20px -6px rgba(15, 23, 42, 0.4);
        }
        .rs-balance-item {
          text-align: center;
        }
        .rs-balance-label {
          font-size: 0.68rem;
          color: #94a3b8;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }
        .rs-balance-value {
          font-size: 1.4rem;
          font-weight: 700;
          letter-spacing: -0.02em;
        }
        .rs-balance-divider {
          width: 1px;
          height: 40px;
          background: rgba(255,255,255,0.1);
        }

        /* ========== FORM CARD ========== */
        .rs-form-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .rs-form-header {
          padding: 20px 28px;
          background: linear-gradient(180deg, #ffffff, #fafbfc);
          border-bottom: 1px solid #f1f5f9;
        }
        .rs-form-icon {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          background: #eff6ff;
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .rs-form-title {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.95rem;
          margin: 0;
        }
        .rs-form-subtitle {
          color: #94a3b8;
          font-size: 0.75rem;
        }
        .rs-form-body {
          padding: 28px;
        }

        /* ========== SECTIONS ========== */
        .rs-section {
          margin-bottom: 32px;
        }
        .rs-section:last-child {
          margin-bottom: 0;
        }
        .rs-section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-weight: 700;
          color: #0f172a;
          font-size: 1rem;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid #f1f5f9;
        }
        .rs-section-number {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          color: white;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        /* ========== FORM ========== */
        .rs-form-group {
          margin-bottom: 20px;
        }
        .rs-form-label {
          display: block;
          font-weight: 700;
          color: #0f172a;
          font-size: 0.82rem;
          margin-bottom: 8px;
        }
        .rs-required {
          color: #ef4444;
        }
        .rs-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .rs-input-icon {
          position: absolute;
          left: 16px;
          color: #94a3b8;
          font-size: 1.05rem;
          pointer-events: none;
          z-index: 5;
        }
        .rs-input, .rs-select {
          width: 100%;
          padding: 12px 16px 12px 46px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          font-size: 0.875rem;
          color: #0f172a;
          background: white;
          transition: all 0.2s;
          outline: none;
          font-family: inherit;
        }
        .rs-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2394a3b8'%3E%3Cpath d='M12 16L6 10H18L12 16Z'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 20px;
          cursor: pointer;
          padding-right: 40px;
        }
        .rs-input:focus, .rs-select:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }
        .rs-input:disabled {
          background: #f1f5f9;
          color: #94a3b8;
          cursor: not-allowed;
        }
        .rs-input-with-suffix {
          padding-right: 60px;
        }
        .rs-input-suffix {
          position: absolute;
          right: 16px;
          color: #64748b;
          font-weight: 700;
          font-size: 0.8rem;
          pointer-events: none;
        }
        .rs-input-spinner {
          position: absolute;
          right: 46px;
        }

        /* ========== TABS ========== */
        .rs-tabs {
          display: inline-flex;
          background: #f1f5f9;
          border-radius: 12px;
          padding: 5px;
          gap: 4px;
          margin-bottom: 20px;
        }
        .rs-tab {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          border: none;
          background: transparent;
          color: #64748b;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
          font-family: inherit;
        }
        .rs-tab:hover {
          color: #3b82f6;
        }
        .rs-tab.active {
          background: white;
          color: #3b82f6;
          box-shadow: 0 2px 6px rgba(0,0,0,0.06);
        }

        /* ========== PACKS ========== */
        .rs-packs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 14px;
          margin-bottom: 20px;
        }
        .rs-pack-card {
          position: relative;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 14px;
          padding: 20px 16px;
          text-align: center;
          cursor: pointer;
          transition: all 0.25s;
        }
        .rs-pack-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 24px -8px rgba(0,0,0,0.12);
        }
        .rs-pack-card.selected {
          background: var(--pack-bg);
          border-color: var(--pack-color);
          transform: translateY(-3px);
          box-shadow: 0 12px 24px -8px var(--pack-color);
        }
        .rs-pack-badge-popular {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          font-size: 0.65rem;
          font-weight: 700;
          padding: 4px 12px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 4px;
          box-shadow: 0 4px 10px -2px rgba(245, 158, 11, 0.5);
          white-space: nowrap;
        }
        .rs-pack-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          margin-bottom: 12px;
        }
        .rs-pack-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
          margin-bottom: 8px;
        }
        .rs-pack-sms {
          font-size: 1.6rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1;
        }
        .rs-pack-label {
          font-size: 0.7rem;
          color: #94a3b8;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-top: 4px;
        }
        .rs-pack-check {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
        }

        /* ========== CUSTOM ========== */
        .rs-custom-section {
          text-align: center;
          padding: 32px 20px;
          background: #f8fafc;
          border-radius: 14px;
          margin-bottom: 20px;
        }
        .rs-custom-icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #eff6ff;
          color: #3b82f6;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          margin-bottom: 16px;
        }
        .rs-custom-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 8px;
        }
        .rs-custom-text {
          color: #64748b;
          font-size: 0.85rem;
          margin-bottom: 20px;
        }
        .rs-custom-input-wrapper {
          position: relative;
          display: inline-flex;
          align-items: center;
          max-width: 400px;
          width: 100%;
        }

        /* ========== SUMMARY ========== */
        .rs-summary {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #bbf7d0;
          margin-top: 20px;
        }
        .rs-summary-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
          color: #065f46;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #bbf7d0;
        }
        .rs-summary-header > i {
          color: #10b981;
          font-size: 1.2rem;
        }
        .rs-summary-items {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        .rs-summary-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 12px;
          background: white;
          border-radius: 10px;
          border: 1px solid #d1fae5;
        }
        .rs-summary-item > i {
          color: #10b981;
          font-size: 1.2rem;
          margin-bottom: 4px;
        }
        .rs-summary-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 1.1rem;
          line-height: 1;
        }
        .rs-summary-label {
          font-size: 0.68rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .rs-summary-item-price {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-color: #3b82f6;
        }
        .rs-summary-item-price > i,
        .rs-summary-item-price .rs-summary-value {
          color: white;
        }
        .rs-summary-item-price .rs-summary-label {
          color: rgba(255,255,255,0.8);
        }
        .rs-summary-footer {
          text-align: center;
          font-size: 0.78rem;
          color: #065f46;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #bbf7d0;
        }

        /* ========== METHODS ========== */
        .rs-methods-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 14px;
        }
        .rs-method-card {
          position: relative;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 14px;
          padding: 22px 16px;
          text-align: center;
          cursor: pointer;
          transition: all 0.25s;
        }
        .rs-method-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 24px -8px rgba(0,0,0,0.12);
        }
        .rs-method-card.selected {
          transform: translateY(-3px);
          box-shadow: 0 12px 24px -8px rgba(0,0,0,0.15);
        }
        .rs-method-icon {
          font-size: 2.2rem;
          margin-bottom: 12px;
          transition: all 0.2s;
        }
        .rs-method-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
          margin-bottom: 4px;
        }
        .rs-method-desc {
          font-size: 0.75rem;
          color: #94a3b8;
        }
        .rs-method-check {
          position: absolute;
          top: 10px;
          right: 10px;
          font-size: 1.2rem;
        }

        /* ========== DETAILS SECTION ========== */
        .rs-details-section {
          background: #f8fafc;
          border-radius: 14px;
          padding: 24px;
          border: 1px solid #e2e8f0;
        }

        .rs-wallets-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 12px;
        }
        .rs-wallet-card {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 14px 18px;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .rs-wallet-card:hover {
          border-color: #3b82f6;
          background: #eff6ff;
        }
        .rs-wallet-card.selected {
          border-color: #3b82f6;
          background: #eff6ff;
          box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.3);
        }
        .rs-wallet-logo {
          flex-shrink: 0;
        }
        .rs-network-logo {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          object-fit: contain;
        }
        .rs-network-emoji {
          font-size: 1.5rem;
        }
        .rs-wallet-info {
          flex-grow: 1;
          min-width: 0;
        }
        .rs-wallet-name {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.85rem;
          margin-bottom: 2px;
        }
        .rs-wallet-code {
          font-size: 0.7rem;
          color: #64748b;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }
        .rs-wallet-check {
          color: #3b82f6;
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        /* ========== FORM ROW ========== */
        .rs-form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        /* ========== ACTION BUTTON ========== */
        .rs-action-section {
          text-align: center;
          padding-top: 20px;
          border-top: 2px dashed #f1f5f9;
          margin-top: 20px;
        }
        .rs-btn-primary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 12px 24px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          transition: all 0.2s;
          cursor: pointer;
        }
        .rs-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-2px);
          box-shadow: 0 12px 24px -6px rgba(59, 130, 246, 0.5);
        }
        .rs-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .rs-btn-secondary {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 24px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.875rem;
          background: white;
          border: 1px solid #e2e8f0;
          color: #334155;
          cursor: pointer;
          transition: all 0.2s;
        }
        .rs-btn-secondary:hover {
          background: #f1f5f9;
        }
        .rs-btn-large {
          padding: 16px 40px;
          border-radius: 50px;
          font-size: 1rem;
        }

        /* ========== ALERTS ========== */
        .rs-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .rs-alert > i {
          font-size: 1.2rem;
          flex-shrink: 0;
        }
        .rs-alert-success {
          background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
          color: #065f46;
          border-left: 4px solid #10b981;
        }
        .rs-alert-success > i {
          color: #10b981;
        }
        .rs-alert-danger {
          background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
          color: #991b1b;
          border-left: 4px solid #ef4444;
        }
        .rs-alert-danger > i {
          color: #ef4444;
        }
        .rs-alert-warning {
          background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
          color: #92400e;
          border-left: 4px solid #f59e0b;
        }
        .rs-alert-warning > i {
          color: #f59e0b;
        }
        .rs-alert-info {
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          color: #1e40af;
          border-left: 4px solid #3b82f6;
        }
        .rs-alert-info > i {
          color: #3b82f6;
        }

        /* ========== MODAL ========== */
        .rs-modal .modal-content {
          border: none;
          border-radius: 16px;
          overflow: hidden;
        }
        .rs-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          color: white;
        }
        .rs-modal-header-blue {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        }
        .rs-modal-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(255,255,255,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }
        .rs-modal-title {
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
        }
        .rs-modal-subtitle {
          color: rgba(255,255,255,0.85);
          font-size: 0.8rem;
        }
        .rs-modal-close {
          background: rgba(255,255,255,0.1);
          border: none;
          color: white;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .rs-modal-close:hover {
          background: rgba(255,255,255,0.2);
          transform: rotate(90deg);
        }
        .rs-modal-body {
          padding: 24px;
          background: #f8fafc;
        }
        .rs-modal-footer {
          padding: 16px 24px;
          background: white;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        /* Confirm */
        .rs-confirm-amount {
          text-align: center;
          padding: 20px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 14px;
          margin-bottom: 20px;
          border: 1px solid #bfdbfe;
        }
        .rs-confirm-amount-label {
          font-size: 0.72rem;
          color: #64748b;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 8px;
        }
        .rs-confirm-amount-value {
          font-size: 2rem;
          font-weight: 700;
          color: #2563eb;
          letter-spacing: -0.02em;
        }
        .rs-confirm-details {
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .rs-confirm-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 18px;
          border-bottom: 1px solid #f1f5f9;
        }
        .rs-confirm-row:last-child {
          border-bottom: none;
        }
        .rs-confirm-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #64748b;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .rs-confirm-label > i {
          color: #94a3b8;
        }
        .rs-confirm-value {
          font-weight: 700;
          color: #0f172a;
          font-size: 0.9rem;
        }

        /* Success/Error */
        .rs-success-body, .rs-error-body {
          padding: 40px 24px;
          text-align: center;
        }
        .rs-success-icon, .rs-error-icon {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin-bottom: 20px;
        }
        .rs-success-icon {
          background: linear-gradient(135deg, #d1fae5, #a7f3d0);
          color: #10b981;
          box-shadow: 0 12px 24px -8px rgba(16, 185, 129, 0.4);
        }
        .rs-error-icon {
          background: linear-gradient(135deg, #fee2e2, #fecaca);
          color: #ef4444;
          box-shadow: 0 12px 24px -8px rgba(239, 68, 68, 0.4);
        }
        .rs-success-title, .rs-error-title {
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 12px;
        }
        .rs-success-text, .rs-error-text {
          color: #64748b;
          font-size: 0.9rem;
          margin: 0;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .rs-summary-items {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 768px) {
          .rs-header {
            padding: 20px;
          }
          .rs-header-title {
            font-size: 1.2rem;
          }
          .rs-form-body {
            padding: 20px;
          }
          .rs-balance-card {
            flex-direction: column;
            gap: 12px;
          }
          .rs-balance-divider {
            width: 40px;
            height: 1px;
          }
          .rs-packs-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .rs-methods-grid {
            grid-template-columns: 1fr;
          }
          .rs-modal-footer {
            flex-direction: column;
          }
          .rs-modal-footer > * {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}